import { useGetOne, useTranslate } from 'ra-core';
import { useParams } from 'react-router-dom';
import { AIGeneratedInsightsCard } from './Operations/AIGeneratedInsightsCard';
import { KPIPerformanceOverviewCard } from './Operations/KPIPerformanceOverviewCard';
import { RecentInsightsCard } from './Operations/RecentInsightsCard';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useDataProvider } from 'ra-core';
import { type AdminDataProvider } from '../../../api/adminDataProvider';
import {
  type UnitPerformanceDataResponseEntry,
  type UnitPerformanceInsightsResponse,
  type UnitPerformanceLlmInsightResponse,
} from '../../../api/customEndpoints/fetchUnitPerformanceScores';
import { InsightsCard } from './Operations/InsightsCard';
import { MoonstarTitle } from '../../Moonstar/MoonstarTitle';
import { GenerateInsightsControl } from './GenerateInsightsControl';
import { useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Error } from '../../../components/admin/error';
import { Loading } from '../../../components/admin/loading';

export function UnitInsightsPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const numericUnitId = Number(unitId);
  const dataProvider = useDataProvider<AdminDataProvider>();
  const translate = useTranslate();

  const unit = useGetOne<{ id: number; name: string }>('units', {
    id: numericUnitId,
  });

  const unitLlmInsights = useQuery<UnitPerformanceLlmInsightResponse>({
    queryKey: ['unitLlmInsights', numericUnitId],
    queryFn: async () =>
      dataProvider.fetchUnitPerformanceLlmInsights(numericUnitId),
  });

  const unitInsights = useQuery<UnitPerformanceInsightsResponse>({
    queryKey: ['unitInsights', numericUnitId],
    queryFn: async () =>
      dataProvider.fetchUnitPerformanceInsights(numericUnitId),
  });

  const unitKpiData = useQuery({
    queryKey: ['unitKpiData', numericUnitId],
    queryFn: async () => dataProvider.fetchUnitPerformanceData(numericUnitId),
  });

  return (
    <>
      <MoonstarTitle
        breadcrumb={[
          translate('resources.units.name', { count: 2 }),
          unit.data?.name || translate('resources.units.name', { count: 1 }),
          translate('resources.units.fields.performance'),
          translate('moonstar.unitPerformance.insights.title'),
        ]}
      />

      {unit.isPending ? (
        <Loading />
      ) : unit.isError ? (
        <Error
          error={unit.error}
          resetErrorBoundary={() => void unit.refetch()}
        />
      ) : (
        <UnitInsightsDisplay
          unit={unit.data}
          unitInsights={unitInsights}
          unitKpiData={unitKpiData}
          unitLlmInsights={unitLlmInsights}
        />
      )}
    </>
  );
}

function UnitInsightsDisplay({
  unit,
  unitInsights,
  unitKpiData,
  unitLlmInsights,
}: {
  unit: { id: number; name: string };
  unitInsights: UseQueryResult<UnitPerformanceInsightsResponse>;
  unitKpiData: UseQueryResult<UnitPerformanceDataResponseEntry[]>;
  unitLlmInsights: UseQueryResult<UnitPerformanceLlmInsightResponse>;
}) {
  const [pendingLlmInsights, setPendingLlmInsights] = useState(false);
  const [streamedInsights, setStreamedInsights] = useState('');
  const translate = useTranslate();

  return (
    <div className="space-y-2 p-1">
      <h1 className="text-xl font-semibold">
        {translate('moonstar.unitPerformance.insights.pageTitle', {
          name: unit.name,
        })}
      </h1>
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Card>
            <AIGeneratedInsightsCard
              unitLlmInsights={unitLlmInsights}
              streamedLlmInsights={streamedInsights || undefined}
              pendingLlmInsights={pendingLlmInsights}
            />

            <GenerateInsightsControl
              unitId={String(unit.id)}
              insightId={unitLlmInsights.data?.id}
              setPendingLlmInsights={(pending) => {
                setPendingLlmInsights(pending);

                if (pending) {
                  setStreamedInsights('');
                }
              }}
              onTextStream={(chunk) =>
                setStreamedInsights((prev) => prev + chunk)
              }
              onTextStreamEnd={() => {
                void unitLlmInsights.refetch();
              }}
            />
          </Card>
          <Card>
            <InsightsCard
              unit={unit}
              unitLlmInsights={unitLlmInsights}
              unitInsights={unitInsights}
            />
          </Card>
        </div>
        <div className="space-y-4">
          <Card>
            <KPIPerformanceOverviewCard kpiData={unitKpiData} />
          </Card>
          <Card>
            <RecentInsightsCard unitInsights={unitInsights} />
          </Card>
        </div>
      </div>
    </div>
  );
}
